import { Role } from '@form-factory/schema/enums';
import { Trans } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { Badge, Dropdown, Menu as UIMenu } from '@utima/ui';
import {
  CircleHelp,
  Building,
  ChevronsUpDown,
  PlusCircle,
  LibraryBig,
  BugPlayIcon,
  Inbox,
  Edit,
  UserRoundPen,
  Users,
} from 'lucide-react';
import { memo, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useInboxUnread } from '@/components/inbox/hooks/useInboxUnread';
import { AppSettings } from '@/constants/AppSettings';
import { useUser } from '@/hooks';
import { useOrganizationChange } from '@/hooks/useOrganizationChange';
import { trpc } from '@/services/trpc';
import { useBoundStore } from '@/store/store';

import { ChatsSwitch } from './chats-switch';
import { MenuLink } from './MenuLink';
import { Protected } from '../protected/Protected';

export type MenuProps = {};

export const Menu = memo(function Menu({}: MenuProps) {
  const { data: chats } = trpc.chats.findAll.useQuery();
  const { data: organizations } = trpc.organizations.findAll.useQuery();
  const { setChat, chat: currentChat } = useBoundStore(
    useShallow(state => ({
      setChat: state.setChat,
      chat: state.chat,
    })),
  );

  const { currentOrganizationId } = useUser();
  const { unreadCount } = useInboxUnread(true);
  const handleOrganizationChange = useOrganizationChange();

  const currentOrganizationName = organizations?.items?.find(
    organization => organization.id === currentOrganizationId,
  )?.name;

  useEffect(() => {
    if (!chats?.items?.length) {
      return;
    }

    // If there is no current chat, set the first chat as the current chat
    if (!currentChat) {
      setChat(chats?.items?.[0] as any);
    }

    // If the current chat is not in the list of chats, set the first chat as the current chat
    if (
      currentChat &&
      !chats?.items?.find(chat => chat.id === currentChat.id)
    ) {
      setChat(chats?.items?.[0] as any);
    }

    // Update existing chat, with new data when refetched
    if (currentChat) {
      const chat = chats?.items?.find(chat => chat.id === currentChat.id);

      if (chat) {
        setChat(chat as any);
      }
    }
  }, [chats, currentChat, setChat]);

  const chatId = currentChat?.id;

  return (
    <UIMenu.Root>
      <UIMenu.Content>
        <UIMenu.Item>
          <MenuLink to='/inbox' Icon={Inbox}>
            <div className='flex w-full items-center justify-between'>
              <Trans>Inbox</Trans>
              {unreadCount > 0 && (
                <Badge
                  className='min-w-5 justify-center'
                  size='xs'
                  variant='primary'
                >
                  {unreadCount}
                </Badge>
              )}
            </div>
          </MenuLink>
        </UIMenu.Item>
        <UIMenu.Item>
          <MenuLink to='/registrations' Icon={UserRoundPen}>
            <Trans>Registrations</Trans>
          </MenuLink>
        </UIMenu.Item>
        <UIMenu.Item>
          <MenuLink to='/clubs' Icon={Building}>
            <Trans>Clubs</Trans>
          </MenuLink>
        </UIMenu.Item>
        <UIMenu.Item>
          <MenuLink to='/users' Icon={Users}>
            <div className='flex w-full items-center justify-between'>
              <Trans>Administrators</Trans>
            </div>
          </MenuLink>
        </UIMenu.Item>
        <UIMenu.Label className='mt-6'>
          <Trans>Agent</Trans>
        </UIMenu.Label>
        <UIMenu.Item>
          <ChatsSwitch />
        </UIMenu.Item>

        {chatId && (
          <>
            <UIMenu.Item>
              <MenuLink
                to='/chats/$chatId/documents'
                params={{
                  chatId: chatId,
                }}
                Icon={LibraryBig}
              >
                <Trans>Data sources</Trans>
              </MenuLink>
            </UIMenu.Item>
            <UIMenu.Item>
              <MenuLink
                to='/chats/$chatId/edit'
                params={{
                  chatId: chatId,
                }}
                Icon={Edit}
              >
                <Trans>Configure</Trans>
              </MenuLink>
            </UIMenu.Item>
            <UIMenu.Item>
              <MenuLink
                to='/chats/$chatId/faqs'
                Icon={CircleHelp}
                params={{
                  chatId: chatId,
                }}
              >
                <Trans>FAQs</Trans>
              </MenuLink>
            </UIMenu.Item>
            <UIMenu.Item>
              <UIMenu.Link asChild>
                <a
                  target='_blank'
                  href={`${AppSettings.playgroundUrl}?chatId=${chatId}`}
                  className='!text-green-500'
                  rel='noreferrer'
                >
                  <BugPlayIcon className='size-4 transition-none' />
                  <Trans>Playground</Trans>
                </a>
              </UIMenu.Link>
            </UIMenu.Item>
          </>
        )}

        <UIMenu.Label className='mt-6'>
          <Trans>Organizations</Trans>
        </UIMenu.Label>
        <UIMenu.Item>
          <Dropdown.Root modal={false}>
            <Dropdown.Trigger className='rounded-radius border-border hover:bg-accent m-1 flex w-full items-center justify-between border px-3 py-2.5 text-sm font-medium transition-all'>
              {currentOrganizationName ?? <Trans>Choose organization</Trans>}
              <ChevronsUpDown className='size-4' />
            </Dropdown.Trigger>
            <Dropdown.Content
              side='bottom'
              align='end'
              className='max-h-60 min-w-52 overflow-y-auto'
            >
              {organizations?.items?.length && (
                <>
                  <Dropdown.Label>
                    <Trans>Organizations</Trans>
                  </Dropdown.Label>
                  <Dropdown.Group>
                    {organizations?.items?.map(organization => (
                      <Dropdown.Item
                        className='flex items-center justify-between'
                        key={organization.id}
                        onClick={() =>
                          handleOrganizationChange(organization.id)
                        }
                      >
                        {organization.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Group>
                  <Dropdown.Separator />
                </>
              )}
              <Dropdown.Label>
                <Trans>Management</Trans>
              </Dropdown.Label>
              <Dropdown.Group>
                <Dropdown.Item asChild>
                  <Link
                    data-cta='admin-menu-organizations-add-new-link'
                    to='/organizations/create'
                  >
                    <PlusCircle className='mr-2 size-4' />
                    <Trans>Add new</Trans>
                  </Link>
                </Dropdown.Item>
              </Dropdown.Group>
            </Dropdown.Content>
          </Dropdown.Root>
        </UIMenu.Item>

        <Protected roles={[Role.SuperAdmin]}>
          <UIMenu.Label className='mt-6 text-amber-600'>
            <Trans>Superadmin</Trans>
          </UIMenu.Label>
          <UIMenu.Item>
            <MenuLink
              to='/organizations'
              Icon={Building}
              activeProps={{ className: 'bg-amber-500/30' }}
            >
              <Trans>Organizations</Trans>
            </MenuLink>
          </UIMenu.Item>
        </Protected>
      </UIMenu.Content>
    </UIMenu.Root>
  );
});
